import { useId, useState, type ReactNode } from "react"

import {
  Button,
  Divider,
  Input,
  Label,
  Select,
  SelectContent,
  SelectionChip,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SuggestionInput,
  TruncatedText,
} from "@coval/ui"
import { Plus, X } from "lucide-react"

import {
  formatMetadataFilter,
  getMetadataFilterValidationError,
  MAX_METADATA_FILTERS,
  removeMetadataFilterValue,
} from "@/lib/metadataFilters"
import type { MetadataFilter } from "@/types/MetadataFilter"

import { MetadataFilterValueInput } from "./MetadataFilterValueInput"

const MATCH_TYPE_OPTIONS = [
  { value: "EQUALS", label: "IS", symbol: "=" },
  { value: "CONTAINS", label: "CONTAINS", symbol: "~" },
  { value: "ANY_OF", label: "IS ONE OF", symbol: "∈" },
] as const

interface MetadataFilterEditorProps {
  filters: MetadataFilter[]
  onAddFilter: (filter: MetadataFilter) => void
  onChangeFilters: (filters: MetadataFilter[]) => void
  keyOptions?: string[]
  valueOptions?: string[]
  onKeyChange?: (key: string) => void
  onValueChange?: (value: string) => void
  suggestionsStatus?: ReactNode
  addLabel?: string
  trimScalarDraft?: boolean
  scalarMaxLength?: number
}

export function MetadataFilterEditor({
  filters,
  onAddFilter,
  onChangeFilters,
  keyOptions,
  valueOptions,
  onKeyChange,
  onValueChange,
  suggestionsStatus,
  addLabel = "Add filter",
  trimScalarDraft = false,
  scalarMaxLength,
}: MetadataFilterEditorProps) {
  const id = useId()
  const [key, setKey] = useState("")
  const [value, setValue] = useState("")
  const [values, setValues] = useState<string[]>([])
  const [matchType, setMatchType] =
    useState<MetadataFilter["matchType"]>("EQUALS")
  const isAnyOf = matchType === "ANY_OF"
  const atFilterLimit = filters.length >= MAX_METADATA_FILTERS
  const changeValue = (next: string) => {
    setValue(next)
    onValueChange?.(next)
  }
  const changeKey = (next: string) => {
    setKey(next)
    onKeyChange?.(next)
  }
  const filterKey = trimScalarDraft ? key.trim() : key
  const filterValue = !isAnyOf && trimScalarDraft ? value.trim() : value
  const scalarDraftTooLong =
    !isAnyOf &&
    scalarMaxLength !== undefined &&
    (key.length > scalarMaxLength || value.length > scalarMaxLength)
  const canAdd =
    filterKey.length > 0 &&
    !atFilterLimit &&
    !scalarDraftTooLong &&
    (isAnyOf ? values.length > 0 && value.length === 0 : filterValue.length > 0)
  const add = () => {
    if (!canAdd) {
      return
    }
    onAddFilter(
      isAnyOf
        ? { key: filterKey, value: "", matchType, values }
        : { key: filterKey, value: filterValue, matchType },
    )
    changeKey("")
    changeValue("")
    setValues([])
    setMatchType("EQUALS")
  }

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-1">
        <Label htmlFor={`${id}-key`}>Key</Label>
        {keyOptions ? (
          <SuggestionInput
            id={`${id}-key`}
            aria-label="Metadata key"
            value={key}
            onValueChange={changeKey}
            options={keyOptions}
            placeholder="Choose or type a key..."
          />
        ) : (
          <Input
            id={`${id}-key`}
            maxLength={isAnyOf ? undefined : scalarMaxLength}
            value={key}
            onChange={(event) => changeKey(event.target.value)}
            placeholder="Metadata key"
          />
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${id}-match-type`}>Condition</Label>
        <Select
          value={matchType}
          onValueChange={(next) => {
            if (next !== matchType) {
              setMatchType(next as MetadataFilter["matchType"])
              setValues([])
            }
          }}
        >
          <SelectTrigger
            id={`${id}-match-type`}
            aria-label="Metadata match type"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MATCH_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  <span
                    aria-hidden="true"
                    className="w-4 text-center font-mono text-coval-text-muted"
                  >
                    {option.symbol}
                  </span>
                  <span>{option.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <MetadataFilterValueInput
        id={`${id}-value`}
        isAnyOf={isAnyOf}
        value={value}
        values={values}
        onValueChange={changeValue}
        onValuesChange={setValues}
        onAddFilter={add}
        valueOptions={valueOptions}
        scalarMaxLength={scalarMaxLength}
      />
      {suggestionsStatus}
      {scalarDraftTooLong && (
        <p role="alert" className="text-xs text-coval-text-muted">
          Metadata keys and values must contain at most {scalarMaxLength}{" "}
          characters.
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-coval-text-muted">
          Filter rows are combined with AND.
        </p>
        <Button
          variant="secondary"
          onClick={add}
          disabled={!canAdd}
          aria-label="Add metadata filter"
        >
          <Plus className="h-3 w-3" />
          {addLabel}
        </Button>
      </div>
      {getMetadataFilterValidationError(filters) && (
        <p role="alert" className="text-xs text-coval-text-muted">
          {getMetadataFilterValidationError(filters)}
        </p>
      )}
      {atFilterLimit && (
        <p role="status" className="text-xs text-coval-text-muted">
          Use at most 50 metadata filters. Remove a filter to add another.
        </p>
      )}
      {filters.length > 0 && (
        <>
          <Divider />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Active Filters</span>
              <Button
                variant="ghost"
                onClick={() => onChangeFilters([])}
                aria-label="Clear all metadata filters"
              >
                Clear All
              </Button>
            </div>
            {filters.map((filter, index) => {
              const condition =
                MATCH_TYPE_OPTIONS.find(
                  (option) => option.value === filter.matchType,
                ) ?? MATCH_TYPE_OPTIONS[0]
              return (
                <div
                  key={index}
                  className="space-y-2 rounded-lg bg-surface-1 p-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <TruncatedText className="flex-1 text-sm font-medium">
                      {filter.key}
                    </TruncatedText>
                    <span className="shrink-0 whitespace-nowrap text-xs text-coval-text-muted">
                      <span aria-hidden="true">{condition.symbol} </span>
                      {condition.label}
                    </span>
                    {filter.matchType !== "ANY_OF" && (
                      <TruncatedText className="flex-1 text-sm font-medium">
                        {filter.value}
                      </TruncatedText>
                    )}
                    <Button
                      icon
                      variant="ghost"
                      tooltip={`Remove filter ${formatMetadataFilter(filter)}`}
                      onClick={() =>
                        onChangeFilters(filters.filter((_, i) => i !== index))
                      }
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  {filter.matchType === "ANY_OF" && (
                    <div className="flex flex-wrap gap-2">
                      {(filter.values ?? []).map((item, valueIndex) => (
                        <SelectionChip
                          key={valueIndex}
                          className="max-w-full"
                          label={item === "" ? "(empty)" : item}
                          removeLabel={`Remove ${filter.key} value ${item === "" ? "(empty)" : item}`}
                          onRemove={() =>
                            onChangeFilters(
                              removeMetadataFilterValue(
                                filters,
                                index,
                                valueIndex,
                              ),
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
